import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  InputLabel,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { Supplier, SupplierDiscountRule, DiscountRuleType } from "../types/api";
import {
  getDiscountRules,
  createDiscountRule,
  deleteDiscountRule,
} from "../services/supplier.service";

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (supplier: Supplier) => void;
  editMode?: boolean;
  initialData?: Supplier;
}

export const CreateSupplierModal = (props: Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const initialFormData: Supplier = {
    name: "",
    salesRep: "",
    salesRepEmail: "",
    salesRepPhone: "",
  };

  const [formData, setFormData] = useState<Supplier>(initialFormData);
  const [discountRules, setDiscountRules] = useState<SupplierDiscountRule[]>([]);
  const [newRuleType, setNewRuleType] = useState<string>(DiscountRuleType.FREE_DELIVERY_THRESHOLD);
  const [newThreshold, setNewThreshold] = useState<string>("");

  useEffect(() => {
    if (props.editMode && props.initialData) {
      setFormData({
        name: props.initialData.name || "",
        salesRep: props.initialData.salesRep || "",
        salesRepEmail: props.initialData.salesRepEmail || "",
        salesRepPhone: props.initialData.salesRepPhone || "",
        guid: props.initialData.guid,
        id: props.initialData.id,
      });

      if (props.initialData.guid) {
        getDiscountRules(props.initialData.guid).then(setDiscountRules);
      }
    } else {
      setFormData(initialFormData);
      setDiscountRules([]);
    }
  }, [props.editMode, props.initialData, props.open]);

  const handleAddRule = async () => {
    if (!props.initialData?.guid || !newThreshold) return;
    try {
      const rule = await createDiscountRule(props.initialData.guid, {
        ruleType: newRuleType,
        configuration: { threshold: Math.round(parseFloat(newThreshold) * 100) },
      });
      setDiscountRules([...discountRules, rule]);
      setNewThreshold("");
    } catch (e: any) {
      console.error(e);
    }
  };

  const handleDeleteRule = async (ruleGuid: string) => {
    if (!props.initialData?.guid) return;
    try {
      await deleteDiscountRule(props.initialData.guid, ruleGuid);
      setDiscountRules(discountRules.filter((r) => r.guid !== ruleGuid));
    } catch (e: any) {
      console.error(e);
    }
  };

  const _onChange = (e: any) => {
    const k = e.target.id || e.target.name;
    const v = e.target.value;
    setFormData({ ...formData, [k]: v });
  };

  const _save = () => {
    // When editing, an emptied field is sent as null so the server clears it;
    // undefined would leave the old value in place.
    const empty = props.editMode ? null : undefined;
    const supplierData: Supplier = {
      name: formData.name,
      salesRep: formData.salesRep || empty,
      salesRepEmail: formData.salesRepEmail || empty,
      salesRepPhone: formData.salesRepPhone || empty,
    };

    if (props.editMode && props.initialData) {
      supplierData.guid = props.initialData.guid;
      supplierData.id = props.initialData.id;
    }
    
    setFormData(initialFormData);
    reset();
    props.onSave(supplierData);
  };

  const _onCancel = () => {
    setFormData(initialFormData);
    reset();
    props.onClose();
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      fullWidth
      maxWidth="sm"
    >
      <form
        onSubmit={handleSubmit(
          () => _save(),
          (e) => console.log(e)
        )}
      >
        <DialogTitle>{props.editMode ? "Edit Supplier" : "Create New Supplier"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Note: Suppliers can be renamed and added <u>but not deleted</u>. Note also that renaming a supplier
            will create a new supplier in Xero. To avoid this
            change the name of the supplier in Xero first!
          </DialogContentText>
          <Grid container spacing={2} sx={{ marginTop: 1 }}>
            <Grid size={12}>
              <FormControl fullWidth error={'name' in errors}>
                <TextField
                  {...register("name", { required: true })}
                  required
                  id="name"
                  label="Supplier Name"
                  value={formData.name}
                  onChange={_onChange}
                />
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <TextField
                  id="salesRep"
                  label="Sales Rep"
                  value={formData.salesRep}
                  onChange={_onChange}
                />
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <TextField
                  id="salesRepEmail"
                  label="Sales Rep Email"
                  type="email"
                  value={formData.salesRepEmail}
                  onChange={_onChange}
                />
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <TextField
                  id="salesRepPhone"
                  label="Sales Rep Phone"
                  value={formData.salesRepPhone}
                  onChange={_onChange}
                />
              </FormControl>
            </Grid>

            {props.editMode && (
              <>
                <Grid size={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                    Discount Rules
                  </Typography>
                </Grid>

                {discountRules.map((rule) => (
                  <Grid size={12} key={rule.guid}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">
                        {rule.ruleType === DiscountRuleType.FREE_DELIVERY_THRESHOLD
                          ? `Free Delivery Threshold: £${((rule.configuration as any).threshold / 100).toFixed(2)}`
                          : rule.ruleType}
                      </Typography>
                      <IconButton size="small" onClick={() => handleDeleteRule(rule.guid!)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}

                {discountRules.length === 0 && (
                  <Grid size={12}>
                    <Typography variant="body2" color="text.secondary">
                      No discount rules configured
                    </Typography>
                  </Grid>
                )}

                <Grid size={5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Rule Type</InputLabel>
                    <Select
                      value={newRuleType}
                      label="Rule Type"
                      onChange={(e) => setNewRuleType(e.target.value)}
                    >
                      <MenuItem value={DiscountRuleType.FREE_DELIVERY_THRESHOLD}>
                        Free Delivery Threshold
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={5}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Threshold"
                    type="number"
                    value={newThreshold}
                    onChange={(e) => setNewThreshold(e.target.value)}
                    slotProps={{
                      input: {
                        startAdornment: <InputAdornment position="start">£</InputAdornment>,
                      },
                    }}
                  />
                </Grid>
                <Grid size={2} display="flex" alignItems="center">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAddRule}
                    disabled={!newThreshold}
                  >
                    Add
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="secondary" onClick={_onCancel}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" type="submit">
            {props.editMode ? "Update Supplier" : "Create Supplier"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};